import { toast } from 'react-toastify';

/**
 * Handle API errors and display appropriate toast notifications
 * @param {Error} error - The error object from axios or fetch
 * @param {string} defaultMessage - Default message to show if no specific error message is found
 */
export const handleApiError = (error, defaultMessage = "An error occurred") => {
  console.error("API Error:", error);

  let errorMessage = defaultMessage;

  if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.error) {
    errorMessage = error.response.data.error;
  } else if (error?.response?.statusText) {
    errorMessage = error.response.statusText;
  } else if (error?.message) {
    errorMessage = error.message;
  }

  // Handle specific database constraint errors with user-friendly messages
  if (errorMessage.includes("duplicate key value violates unique constraint")) {
    if (errorMessage.includes("users_username_country_unique")) {
      errorMessage = "This username is already taken for this country. Please choose a different username.";
    } else if (errorMessage.includes("users_email_country_unique")) {
      errorMessage = "This email is already registered for this country. Please use a different email address.";
    } else if (errorMessage.includes("unique constraint")) {
      errorMessage = "This entry already exists. Please check your input and try again.";
    }
  }

  // Handle other common error patterns
  if (errorMessage.includes("ECONNREFUSED") || errorMessage.includes("Network Error")) {
    errorMessage = "Unable to connect to server. Please check your internet connection and try again.";
  } else if (errorMessage.includes("timeout")) {
    errorMessage = "Request timed out. Please try again.";
  } else if (errorMessage.includes("401") || errorMessage.includes("Unauthorized")) {
    errorMessage = "Your session has expired. Please log in again.";
  } else if (errorMessage.includes("403") || errorMessage.includes("Forbidden")) {
    errorMessage = "You don't have permission to perform this action.";
  } else if (errorMessage.includes("404") || errorMessage.includes("Not Found")) {
    errorMessage = "The requested resource was not found.";
  } else if (errorMessage.includes("500") || errorMessage.includes("Internal Server Error")) {
    errorMessage = "Server error occurred. Please try again later.";
  }

  toast.error(errorMessage, {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Show success toast notification
 * @param {string} message - Success message to display
 */
export const showSuccess = (message) => {
  toast.success(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Show info toast notification
 * @param {string} message - Info message to display
 */
export const showInfo = (message) => {
  toast.info(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Show warning toast notification
 * @param {string} message - Warning message to display
 */
export const showWarning = (message) => {
  toast.warning(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
};

/**
 * Utility function to handle API responses consistently
 * @param {Promise} apiCall - The API call promise (axios request)
 * @param {string} successMessage - Message to show on success
 * @param {string} errorMessage - Default error message
 * @returns {Object} { success: boolean, data: any, error: any }
 */
export const handleApiResponse = async (apiCall, successMessage, errorMessage) => {
  try {
    const response = await apiCall;
    if (successMessage) {
      showSuccess(successMessage);
    }
    return { success: true, data: response.data, error: null };
  } catch (error) {
    handleApiError(error, errorMessage);
    return { success: false, data: null, error };
  }
};
